/**
 * Outil « Lignes de niveau et gradient » (Boîte à outils, MPSI/PTSI → PT).
 *
 * Explorer : f(x, y) choisie parmi huit fonctions, ses lignes de niveau (plus
 * foncées pour les valeurs plus grandes) ; un point A qu'on déplace (pas de
 * 0,25) montre ∇f(A) — valeur EXACTE, flèche de longueur fixe — orthogonal à
 * la ligne de niveau passant par A (en couleur), la tangente en A et l'angle
 * droit ; le champ de gradients à la demande. Points critiques : ∇f = 0
 * résolu, nature de chaque point révélée avec la raison (signe de f − f(A)).
 * Défi : dérivée partielle, ∇f(A), orthogonalité, point critique, nature.
 * Logique : `gradient.ts`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { Note, PRIMARY_BUTTON, SECONDARY_BUTTON, Segmented, ToggleChip } from '../../ui/controls';
import { makeFrame, type Frame, type Pt } from '../../ui/frame';
import { formatNumber, frenchSpaces } from '../../ui/format';
import { seededRng } from '../../ui/random';
import {
  DragHandle,
  FIGURE_BG_CLASS,
  HaloText,
  PlotGrid,
  RightAngleMark,
  VectorArrow,
} from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import { add, frac, mul, value } from '../../ui/exact';
import {
  NATURE_TEXT,
  PRESETS,
  QUIZ_KINDS,
  at,
  contour,
  dx,
  dy,
  evalNum,
  hessian,
  intVec,
  lineEq,
  fromDec,
  levels,
  makeQuiz,
  numText,
  polyText,
  presetById,
  type Critical,
  type Preset,
  type Quiz,
  type Seg,
} from './gradient';

const SIZE = 320;
/** A reste dans [−R ; R]² : la flèche et son nom tiennent dans la fenêtre */
const R = 2;
const W = 3.2;
const VP = { xMin: -3.2, xMax: 3.2, yMin: -3.2, yMax: 3.2 };
const SELECT =
  'border-ink-200 text-ink-900 min-h-11 w-full rounded-xl border bg-white px-2 text-[15px]';
const GRAD = 'var(--color-accent-600)';
const LEVEL = 'var(--color-primary-500)';
const n4 = (v: number) => numText(fromDec(v));
const pt = (x: number, y: number) => `(${n4(x)} ; ${n4(y)})`;

const segPath = (segs: Seg[], fr: Frame) =>
  segs
    .map(
      (s) =>
        `M${fr.tx(s.x1).toFixed(1)} ${fr.ty(s.y1).toFixed(1)}L${fr.tx(s.x2).toFixed(1)} ${fr.ty(s.y2).toFixed(1)}`,
    )
    .join('');

/** lignes de niveau de la fonction, une fois par fonction */
function useContours(p: Preset, frame: Frame) {
  return useMemo(() => {
    const f = (x: number, y: number) => evalNum(p.f, x, y);
    // les valeurs critiques sont aussi des niveaux tracés
    const L = levels(
      f,
      -W,
      W,
      10,
      p.criticals.map((c) => evalNum(p.f, c.x, c.y)),
    );
    const span = L.levels.length > 1 ? L.levels.length - 1 : 1;
    return {
      f,
      step: L.step,
      paths: L.levels.map((c, k) => ({
        c,
        d: segPath(contour(f, c, -W, W, 64), frame),
        // plus marqué = valeur plus grande
        opacity: 0.25 + (0.6 * k) / span,
      })),
    };
  }, [p, frame]);
}

function FunctionPicker({ value: v, onChange }: { value: string; onChange: (id: string) => void }) {
  return (
    <label className="block">
      <span className="text-ink-700 mb-1 block text-sm font-semibold">Fonction</span>
      <select className={SELECT} value={v} onChange={(e) => onChange(e.target.value)}>
        {PRESETS.map((p) => (
          <option key={p.id} value={p.id}>
            {frenchSpaces(`f(x, y) = ${polyText(p.f)}`)}
          </option>
        ))}
      </select>
    </label>
  );
}

function Levels({ paths }: { paths: { c: number; d: string; opacity: number }[] }) {
  return (
    <g aria-hidden>
      {paths.map((l) => (
        <path
          key={l.c}
          d={l.d}
          fill="none"
          stroke="var(--color-ink-500)"
          strokeOpacity={l.opacity}
          strokeWidth={1.4}
        />
      ))}
    </g>
  );
}

/* ---------- explorer ---------- */

function Explore({ preset }: { preset: Preset }) {
  const [A, setA] = useState<Pt>({ x: 1, y: 1 });
  const [tangent, setTangent] = useState(true);
  const [field, setField] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const frame = useMemo(() => makeFrame(VP, SIZE), []);
  const { f, paths, step } = useContours(preset, frame);
  const a = at(preset.f, A.x, A.y);
  const fx = value(a.fx);
  const fy = value(a.fy);
  const norm = Math.hypot(fx, fy);
  const fA = value(a.f);
  const levelA = useMemo(() => segPath(contour(f, fA, -W, W, 80), frame), [f, fA, frame]);

  const drag = useDrag<'A'>({
    svgRef,
    frame,
    ids: ['A'],
    pointOf: () => A,
    onDrag: (_, p) => {
      const s = (v: number) => Math.min(R, Math.max(-R, Math.round(v * 4) / 4));
      setA({ x: s(p.x), y: s(p.y) });
    },
    keyStep: 0.25,
  });

  const ax = frame.tx(A.x);
  const ay = frame.ty(A.y);
  const u = frame.unit;
  // direction de ∇f(A) à l'écran (y vers le bas)
  const gx = norm ? fx / norm : 0;
  const gy = norm ? -fy / norm : 0;
  // longueur fixe (1), raccourcie si le bord est plus près (marge pour le nom)
  const room = Math.min(
    gx > 0 ? (W - A.x) / gx : gx < 0 ? (W + A.x) / -gx : Infinity,
    gy < 0 ? (W - A.y) / -gy : gy > 0 ? (W + A.y) / gy : Infinity,
  );
  const len = Math.max(0.6, Math.min(1, room - 0.45));
  const grad = { x: ax + gx * u * len, y: ay + gy * u * len };
  const tan = { x: -gy * u * 1.3, y: gx * u * 1.3 };
  const fieldPts = useMemo(() => {
    const out: { x: number; y: number; ux: number; uy: number }[] = [];
    for (let i = -2.5; i <= 2.5; i += 1)
      for (let j = -2.5; j <= 2.5; j += 1) {
        const v = at(preset.f, i, j);
        const vx = value(v.fx);
        const vy = value(v.fy);
        const n = Math.hypot(vx, vy);
        if (n > 1e-9) out.push({ x: i, y: j, ux: vx / n, uy: vy / n });
      }
    return out;
  }, [preset]);

  const where = `A${pt(A.x, A.y)}`;
  const gradText = `(${numText(a.fx)} ; ${numText(a.fy)})`;
  const X = fromDec(A.x);
  const Y = fromDec(A.y);
  const lines = [
    `f(x, y) = ${polyText(preset.f)}`,
    `∂f/∂x = ${polyText(dx(preset.f))} ; ∂f/∂y = ${polyText(dy(preset.f))}`,
    `En ${where} : f(A) = ${numText(a.f)} et ∇f(A) = ${gradText}.`,
    a.critical
      ? '∇f(A) = 0 : A est un point critique de f. Pas de direction de plus forte pente ici — voir l’onglet « Points critiques ».'
      : `∇f(A) est orthogonal à la ligne de niveau f = ${numText(a.f)} (en couleur) et pointe vers les valeurs croissantes de f.`,
    ...(a.critical
      ? []
      : [
          `Tangente en A à la ligne de niveau : ∂f/∂x(A)(x − ${n4(A.x)}) + ∂f/∂y(A)(y − ${n4(A.y)}) = 0, soit ${lineEq(a.fx, a.fy, add(mul(a.fx, X), mul(a.fy, Y)))} ; elle est dirigée par (−∂f/∂y(A) ; ∂f/∂x(A)), colinéaire à ${intVec(mul(a.fy, frac(-1)), a.fx)}.`,
          `‖∇f(A)‖ ≈ ${formatNumber(norm, 3)} : c’est la plus grande dérivée directionnelle Dᵤf(A) = ⟨∇f(A), u⟩ (u unitaire), atteinte pour u = ∇f(A)/‖∇f(A)‖.`,
        ]),
  ];
  const label = `Lignes de niveau de f(x, y) = ${polyText(preset.f)}. Point ${where}, ∇f(A) = ${gradText}.`;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <ToggleChip on={tangent} onClick={() => setTangent(!tangent)}>
          Tangente en A
        </ToggleChip>
        <ToggleChip on={field} onClick={() => setField(!field)}>
          Champ de gradients
        </ToggleChip>
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="group"
        aria-label={frenchSpaces(label)}
        className={cn(
          'border-ink-100 mx-auto block w-full max-w-[360px] touch-none select-none rounded-2xl border',
          FIGURE_BG_CLASS,
        )}
        {...drag.svgProps}
      >
        <PlotGrid frame={frame} step={1} />
        <Levels paths={paths} />
        {field && (
          <g aria-hidden>
            {fieldPts.map((p) => (
              <VectorArrow
                key={`${p.x};${p.y}`}
                x1={frame.tx(p.x)}
                y1={frame.ty(p.y)}
                x2={frame.tx(p.x + p.ux * 0.4)}
                y2={frame.ty(p.y + p.uy * 0.4)}
                color="var(--color-ink-400)"
                width={1.5}
              />
            ))}
          </g>
        )}
        <path d={levelA} fill="none" stroke={LEVEL} strokeWidth={3} aria-hidden />
        {!a.critical && tangent && (
          <g aria-hidden>
            <line
              x1={ax - tan.x}
              y1={ay - tan.y}
              x2={ax + tan.x}
              y2={ay + tan.y}
              stroke="var(--color-ink-700)"
              strokeWidth={1.75}
              strokeDasharray="6 4"
            />
            <RightAngleMark x={ax} y={ay} ux={gx} uy={gy} vx={tan.x} vy={tan.y} size={20} />
          </g>
        )}
        {!a.critical && (
          <g aria-hidden>
            <VectorArrow x1={ax} y1={ay} x2={grad.x} y2={grad.y} color={GRAD} width={3.5} />
            <HaloText
              x={Math.min(SIZE - 26, Math.max(26, grad.x + gx * 18))}
              y={Math.min(SIZE - 8, Math.max(14, grad.y + gy * 18))}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={14}
              fontWeight={700}
              fill={GRAD}
            >
              ∇f(A)
            </HaloText>
          </g>
        )}
        <DragHandle
          x={ax}
          y={ay}
          color="var(--color-ink-700)"
          label="A"
          labelDx={gx > 0 ? -16 : 16}
          labelDy={gy > 0 ? -14 : 14}
          name={`Point ${where}`}
          active={drag.active === 'A'}
          keyProps={drag.keyProps('A')}
        />
      </svg>
      <p className="sr-only" aria-live="polite">
        {frenchSpaces(`${where}, ∇f(A) = ${gradText}${a.critical ? ' : point critique' : ''}`)}
      </p>
      <Note live={false}>
        {lines.map((l) => (
          <span key={l} data-testid="lvg-line" className="mb-1 block last:mb-0">
            {frenchSpaces(l)}
          </span>
        ))}
      </Note>
      <p className="text-ink-500 text-xs">
        {frenchSpaces(
          `Déplace A (pas de 0,25, dans [−2 ; 2]). Lignes de niveau tous les ${String(step).replace('.', ',')}, plus marquées pour les valeurs plus grandes ; la flèche donne la direction et le sens de ∇f(A), pas sa norme. Là où les lignes se resserrent, ‖∇f‖ est plus grand.`,
        )}
      </p>
    </div>
  );
}

/* ---------- points critiques ---------- */

const NATURE_COLOR: Record<Critical['nature'], string> = {
  min: 'var(--color-success-600)',
  max: 'var(--color-error-500)',
  col: 'var(--color-accent-600)',
  sans: 'var(--color-accent-600)',
};

/** « Hessienne (2e année) : r = 6, s = 0, t = 2 ; det H = rt − s² = 12 > 0 et r > 0 : minimum local. » */
function hessianText(p: Preset, c: Critical) {
  const h = hessian(p.f, c.x, c.y);
  const sign = h.det.p > 0 ? '> 0' : h.det.p < 0 ? '< 0' : '= 0';
  const end =
    h.verdict === 'doute'
      ? 'la hessienne ne permet pas de conclure'
      : h.verdict === 'col'
        ? 'point col'
        : `${h.verdict === 'min' ? 'r > 0' : 'r < 0'} : ${h.verdict === 'min' ? 'minimum' : 'maximum'} local`;
  return `Hessienne (2e année) : r = ∂²f/∂x² = ${numText(h.r)}, s = ∂²f/∂x∂y = ${numText(h.s)}, t = ∂²f/∂y² = ${numText(h.t)} ; det H = rt − s² = ${numText(h.det)}${h.det.p === 0 ? '' : ` ${sign}`}${h.verdict === 'doute' || h.verdict === 'col' ? ' : ' : ' et '}${end}.`;
}

function natureText(c: Critical) {
  const base = NATURE_TEXT[c.nature];
  if (c.nature === 'min' || c.nature === 'max')
    return `${base} ${c.global === false ? 'local' : 'global'}`;
  return base;
}

function Criticals({ preset }: { preset: Preset }) {
  const frame = useMemo(() => makeFrame(VP, SIZE), []);
  const { f, paths } = useContours(preset, frame);
  const [shown, setShown] = useState<number[]>([]);
  // ligne de niveau passant par chaque point critique (une croix en un col)
  // ligne de niveau passant par chaque point critique, PRÈS du point (carré de côté 2,4)
  const through = useMemo(
    () =>
      preset.criticals.map((c) =>
        segPath(
          contour(f, evalNum(preset.f, c.x, c.y), c.x - 1.2, c.x + 1.2, 80, c.y - 1.2, c.y + 1.2),
          frame,
        ),
      ),
    [preset, f, frame],
  );
  const label = `Lignes de niveau de f(x, y) = ${polyText(preset.f)} et ses points critiques : ${preset.criticals
    .map((c, i) => `${pt(c.x, c.y)}${shown.includes(i) ? ` (${natureText(c)})` : ''}`)
    .join(', ')}.`;

  return (
    <div className="space-y-3">
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={frenchSpaces(label)}
        className={cn(
          'border-ink-100 mx-auto block w-full max-w-[360px] rounded-2xl border',
          FIGURE_BG_CLASS,
        )}
      >
        <PlotGrid frame={frame} step={1} />
        <Levels paths={paths} />
        {preset.criticals.map(
          (c, i) =>
            shown.includes(i) && (
              <path
                key={`l${i}`}
                d={through[i]}
                fill="none"
                stroke={NATURE_COLOR[c.nature]}
                strokeWidth={2.5}
                aria-hidden
              />
            ),
        )}
        {preset.criticals.map((c, i) => {
          const on = shown.includes(i);
          const x = frame.tx(c.x);
          const y = frame.ty(c.y);
          return (
            <g key={i} aria-hidden>
              <circle
                cx={x}
                cy={y}
                r={7}
                fill={on ? NATURE_COLOR[c.nature] : 'var(--fig-bg, #fff)'}
                stroke={on ? NATURE_COLOR[c.nature] : 'var(--color-ink-700)'}
                strokeWidth={2.5}
              />
              {on && (
                <HaloText
                  x={x}
                  y={y - 16}
                  textAnchor="middle"
                  fontSize={13}
                  fontWeight={700}
                  fill="var(--color-ink-900)"
                >
                  {NATURE_TEXT[c.nature]}
                </HaloText>
              )}
            </g>
          );
        })}
      </svg>
      <Note live={false}>
        <span data-testid="lvg-solve">{frenchSpaces(preset.solve)}</span>
      </Note>
      <ul className="space-y-2">
        {preset.criticals.map((c, i) => {
          const on = shown.includes(i);
          return (
            <li key={i} className="space-y-2">
              <button
                type="button"
                className={SECONDARY_BUTTON}
                aria-expanded={on}
                onClick={() => setShown(on ? shown.filter((j) => j !== i) : [...shown, i])}
              >
                {frenchSpaces(`Nature en ${pt(c.x, c.y)}`)}
              </button>
              <div aria-live="polite">
                {on && (
                  <Note
                    live={false}
                    tone={c.nature === 'min' || c.nature === 'max' ? 'success' : 'neutral'}
                  >
                    <span data-testid="lvg-nature">
                      {frenchSpaces(`${pt(c.x, c.y)} : ${natureText(c)}. ${c.why}`)}
                    </span>
                    <span data-testid="lvg-hessian" className="mt-1 block">
                      {frenchSpaces(hessianText(preset, c))}
                    </span>
                  </Note>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <p className="text-ink-500 text-xs">
        {frenchSpaces(
          'Pour f de classe C¹ sur un ouvert, un extremum local est un point critique (∇f = 0), mais la réciproque est fausse. Pour conclure, on étudie le signe de f(M) − f(A) près de A : toujours ≥ 0 → minimum, toujours ≤ 0 → maximum, les deux signes → ni l’un ni l’autre. En couleur, près du point : la ligne de niveau qui passe par lui — réduite au point (entourée de lignes fermées) pour un extremum strict, une croix en un point col ; pour x²y + y³, une simple courbe que f traverse en changeant de signe.',
        )}
      </p>
    </div>
  );
}

/* ---------- défi ---------- */

function Challenge({ seed }: { seed?: number }) {
  const rnd = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const rounds = QUIZ_KINDS.length;
  const [count, setCount] = useState(1);
  const [q, setQ] = useState<Quiz>(() => makeQuiz(QUIZ_KINDS[0]!, rnd));
  const [picked, setPicked] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const moved = useRef(false);
  const done = picked !== null && count >= rounds;
  const chosen = picked === null ? null : q.options[picked]!;
  const right = q.options.find((o) => o.correct)!;

  useEffect(() => {
    if (moved.current) titleRef.current?.focus();
    moved.current = false;
  }, [q]);

  const choose = (i: number) => {
    if (picked !== null) return;
    setPicked(i);
    if (q.options[i]!.correct) setScore(score + 1);
  };
  const next = () => {
    const c = done ? 1 : count + 1;
    if (done) setScore(0);
    setCount(c);
    moved.current = true;
    setQ(makeQuiz(QUIZ_KINDS[c - 1]!, rnd));
    setPicked(null);
  };

  return (
    <div className="space-y-3">
      <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
        {frenchSpaces(`Question ${count} / ${rounds} · ${score} juste${score > 1 ? 's' : ''}`)}
      </p>
      <p ref={titleRef} tabIndex={-1} className="text-ink-900 text-base font-semibold outline-none">
        {frenchSpaces(q.prompt)}
      </p>
      <div role="group" aria-label="Réponses" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {q.options.map((o, i) => (
          <button
            key={o.text}
            type="button"
            onClick={() => choose(i)}
            aria-pressed={picked === i}
            aria-disabled={picked !== null && picked !== i}
            className={cn(
              'min-h-11 rounded-xl border px-3 py-2 text-center text-base font-semibold tabular-nums transition-colors',
              picked === null && 'border-ink-200 text-ink-800 hover:bg-ink-50',
              picked !== null && o.correct && 'border-success-600 bg-success-50 text-ink-900',
              picked === i && !o.correct && 'border-error-500 bg-error-50 text-ink-900',
              picked !== null && picked !== i && !o.correct && 'border-ink-100 text-ink-500',
            )}
          >
            {frenchSpaces(o.text)}
          </button>
        ))}
      </div>
      <div aria-live="polite">
        {chosen && (
          <Note live={false} tone={chosen.correct ? 'success' : 'error'}>
            <span data-testid="lvg-feedback">
              {frenchSpaces(
                chosen.correct
                  ? `Exact. ${q.explain}`
                  : `${chosen.hint ?? ''} La bonne réponse : ${right.text}. ${q.explain}`,
              )}
            </span>
          </Note>
        )}
      </div>
      {picked !== null && (
        <div className="flex flex-wrap items-center gap-3">
          {done && (
            <p className="text-ink-900 text-sm font-semibold">
              {frenchSpaces(`Score : ${score} / ${rounds}`)}
            </p>
          )}
          <button type="button" className={done ? SECONDARY_BUTTON : PRIMARY_BUTTON} onClick={next}>
            {done ? 'Recommencer' : 'Question suivante'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------- outil ---------- */

export function LevelGradientTool({ seed }: { /** graine du défi (tests) */ seed?: number }) {
  const [mode, setMode] = useState<'explore' | 'critical' | 'quiz'>('explore');
  const [id, setId] = useState('bowl');
  const preset = presetById(id);
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Partie de l’outil"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'critical', label: 'Points critiques' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      {mode !== 'quiz' && <FunctionPicker value={id} onChange={setId} />}
      <div hidden={mode !== 'explore'}>
        <Explore preset={preset} />
      </div>
      <div hidden={mode !== 'critical'}>
        <Criticals key={preset.id} preset={preset} />
      </div>
      <div hidden={mode !== 'quiz'}>
        <Challenge seed={seed} />
      </div>
    </div>
  );
}
